import {
  type TicketOrderCheckIn,
  type TicketOrderListStatus,
  type TicketOrderSort,
  ticketOrderCheckInValues,
  ticketOrderListStatusValues,
  ticketOrderSortValues,
} from '@pulsify/shared/dto'
import { isUuid } from '@pulsify/shared/slug'
import type { TicketOrdersQuery } from '~/entities/ticket/api/tickets-api'

const SEARCH_MAX_LENGTH = 100

export interface TicketOrdersSearch {
  q?: string
  type?: string
  status?: TicketOrderListStatus
  checkIn?: TicketOrderCheckIn
  sort?: Exclude<TicketOrderSort, 'newest'>
  page?: number
}

type Filters = Omit<TicketOrdersSearch, 'page' | 'sort'> & { sort?: TicketOrderSort }

const pick = <T extends string>(values: readonly T[], value: unknown): T | undefined =>
  values.find((known) => known === value)

function searchText(value: unknown): string {
  const text = typeof value === 'number' || typeof value === 'boolean' ? String(value) : value
  return typeof text === 'string' ? text.trim().slice(0, SEARCH_MAX_LENGTH) : ''
}

export function parseTicketOrdersSearch(search: Record<string, unknown>): TicketOrdersSearch {
  const q = searchText(search.q)
  const type = typeof search.type === 'string' && isUuid(search.type) ? search.type : undefined
  const status = pick(ticketOrderListStatusValues, search.status)
  const checkIn = pick(ticketOrderCheckInValues, search.checkIn)
  const sort = pick(ticketOrderSortValues, search.sort)
  const page = Number(search.page)
  return {
    ...(q ? { q } : {}),
    ...(type ? { type } : {}),
    ...(status ? { status } : {}),
    ...(checkIn ? { checkIn } : {}),
    ...(sort && sort !== 'newest' ? { sort } : {}),
    ...(Number.isInteger(page) && page > 1 ? { page } : {}),
  }
}

export function refineSearch(search: TicketOrdersSearch, change: Filters): TicketOrdersSearch {
  return parseTicketOrdersSearch({ ...search, ...change, page: undefined })
}

export function hasFilters(search: TicketOrdersSearch): boolean {
  return Boolean(search.q || search.type || search.status || search.checkIn)
}

export function ordersQuery(search: TicketOrdersSearch): TicketOrdersQuery {
  return {
    search: search.q,
    typeId: search.type,
    status: search.status,
    checkIn: search.checkIn,
    sort: search.sort ?? 'newest',
    page: search.page ?? 1,
  }
}

export function pageWindow(
  page: number,
  pageSize: number,
  total: number,
): { from: number; to: number; pages: number } {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  if (total === 0) return { from: 0, to: 0, pages }
  return { from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, total), pages }
}
